import { isEmpty } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { Redirect, RouteComponentProps } from 'react-router';

import { Loader } from '@opendoor/bricks';
import * as sessionActions from '@opendoor/shared-fe/build/store/session/actions';
import * as sessionSelectors from '@opendoor/shared-fe/build/store/session/selectors';
import * as Sentry from '@sentry/browser';
import ConfirmationPage, {
  IConfirmationPageProps,
} from '../../components/agentResaleOfferFlow/ConfirmationPage';
import LandingPageV2, {
  ILandingPageV2Props,
} from '../../components/agentResaleOfferFlow/LandingPageV2';
import OfferDetailPage, {
  IOfferDetailPageOnSubmitParams,
  IOfferDetailPageProps,
} from '../../components/agentResaleOfferFlow/OfferDetailPage';
import ResaleOfferPortalError from '../../components/agentResaleOfferFlow/ResaleOfferPortalError';
import { IAgent, IListing, IListingOfferBasic, IRequestStatus } from '../../declarations';
import { MORTGAGE_SENTRY_URL } from '../../helpers/globals';
import * as agentResalePortalActions from '../../store/agentResaleOfferPortal/actions';
import * as agentResalePortalSelectors from '../../store/agentResaleOfferPortal/selectors';
import ErrorPage from '../ErrorPage';

Sentry.init({ dsn: MORTGAGE_SENTRY_URL });

interface IAgentResaleOfferParams {
  page: string;
}

interface IAgentResaleOfferActions extends IOfferDetailPageOnSubmitParams {
  agent: IAgent;
  agentError: boolean;
  deleteDocument: (fileTypeId: string | number) => void;
  documentsWithUploadErrors: Set<string | number>;
  getAgent: (agentFullName: string, agentPhoneNumber: string) => void;
  getListing: (listingSlug: string) => void;
  register: (
    email: string,
    name: string,
    password: string,
    phone: string,
    rememberMe: boolean,
  ) => void;
  setTypedAgentEmail: (typedAgentEmail: string) => void;
  setTypedAgentFullName: (typedAgentFullName: string) => void;
  setTypedAgentPhoneNumber: (typedAgentPhoneNumber: string) => void;
  submitListingOffer: (offerDetails: IOfferDetailPageOnSubmitParams) => void;
  uploadDocument: (document: { file: File; s3Key: string }) => void;
  listing: IListing;
  listingOffer: IListingOfferBasic;
  offerSubmissionCompleted: boolean;
  typedAgentEmail: string;
  typedAgentFullName: string;
  typedAgentPhoneNumber: string;
  isLoadingListing: boolean;
  isLoadingAgent: boolean;
  listingError: boolean;
  listingOfferError: boolean;
  pendingDocumentUploads: boolean;
  registrationRequestStatus: IRequestStatus;
}

export type IAgentResaleOfferPortalProps = RouteComponentProps<IAgentResaleOfferParams> &
  IAgentResaleOfferActions;

export class AgentResaleOfferPortal extends React.PureComponent<IAgentResaleOfferPortalProps> {
  public componentDidMount() {
    const page = this.props.match.params.page;

    // if it's a reserved page, don't do anything
    switch (page) {
      case 'error':
      case 'start':
      case 'confirm':
        return;
    }

    // otherwise, treat :page as a listing slug and use it to fetch the listing
    this.onSubmitLandingPageForm(page);
  }

  public render(): JSX.Element {
    const {
      agentError,
      listing,
      isLoadingAgent,
      isLoadingListing,
      listingError,
      listingOfferError,
      offerSubmissionCompleted,
      typedAgentFullName,
      typedAgentPhoneNumber,
    } = this.props;

    if (isEmpty(this.props.match.params.page)) {
      return <Redirect to="/make-offer/start" />;
    }

    switch (this.props.match.params.page) {
      case 'error':
        return <ErrorPage errorContent={<ResaleOfferPortalError />} />;
      case 'start':
        return <LandingPageV2 {...this.mapStateToOfferLandingPageProps()} />;

      case 'confirm':
        // Case when user comes to this route directly (without trying page 1/2 first)
        if (!typedAgentFullName && !typedAgentPhoneNumber) {
          return <Redirect to="start" />;
        }

        // Case when user submits on page 1, and tries go directly to confirmation
        // Should never happen...
        if (isLoadingAgent || isLoadingListing) {
          return <Redirect to="start" />;
        }

        // Case when user clicks submit, but we are still processing
        // the transaction
        if (!offerSubmissionCompleted) {
          return listingOfferError ? <Redirect to="error" /> : <Loader />;
        }
        return <ConfirmationPage {...this.mapStateToConfirmationPageProps()} />;

      default:
        // Case when user finished page one, but we are still loading information
        if (isLoadingListing) {
          return agentError || listingError ? <Redirect to="error" /> : <Loader />;
        }

        // Case when user hits back button on completed offer
        if (offerSubmissionCompleted) {
          return <Redirect to="confirm" />;
        }

        // Case when none of the above  no listing yet -> retry at start
        if (isEmpty(listing)) {
          return <Redirect to="start" />;
        }

        return <OfferDetailPage {...this.mapStateToOfferDetailPageProps()} />;
    }
  }

  /****** LANDING PAGE HELPERS ********/

  private mapStateToOfferLandingPageProps = (): ILandingPageV2Props => {
    const { isLoadingListing } = this.props;

    return {
      loading: isLoadingListing,
      onSubmit: this.onSubmitLandingPageForm,
    };
  };

  private onSubmitLandingPageForm = (listingSlug: string): void => {
    if (!listingSlug) {
      return;
    }
    const encodedListingSlug = encodeURIComponent(listingSlug);
    this.props.getListing(encodedListingSlug);
    this.props.history.push(`/make-offer/${encodedListingSlug}`);
  };

  /****** OFFER DETAIL PAGE HELPERS ********/

  private mapStateToOfferDetailPageProps = (): IOfferDetailPageProps => {
    const {
      deleteDocument,
      documentsWithUploadErrors,
      history,
      listing,
      pendingDocumentUploads,
      uploadDocument,
    } = this.props;
    return {
      deleteDocument,
      documentsWithUploadErrors,
      history,
      homeAdvisorInfo: listing.resale_agent_lead,
      listingInfo: listing,
      onListingUpdate: this.onListingUpdate,
      onSubmit: this.onSubmitOfferDetailPageForm,
      pendingDocumentUploads,
      uploadDocument,
    };
  };

  private onSubmitOfferDetailPageForm = (offerDetails: IOfferDetailPageOnSubmitParams) => {
    const {
      typedAgentEmail,
      typedAgentFirstName,
      typedAgentLastName,
      typedAgentPhoneNumber,
    } = offerDetails;
    this.props.setTypedAgentEmail(typedAgentEmail);
    this.props.setTypedAgentFullName(`${typedAgentFirstName} ${typedAgentLastName}`);
    this.props.setTypedAgentPhoneNumber(typedAgentPhoneNumber);
    this.props.submitListingOffer(offerDetails);
    this.props.history.push('/make-offer/confirm');
  };

  private onListingUpdate = (listingSlug: string) => {
    this.props.getListing(listingSlug);
  };

  /****** CONFIRMATION PAGE HELPERS ********/

  private mapStateToConfirmationPageProps = (): IConfirmationPageProps => {
    const {
      listing,
      listingOffer,
      register,
      registrationRequestStatus,
      typedAgentEmail,
      typedAgentFullName,
      typedAgentPhoneNumber,
    } = this.props;
    return {
      address: listing.address.street,
      addressToken: listing.address.address_token,
      agentEmail: typedAgentEmail,
      agentFullName: typedAgentFullName,
      agentPhoneNumber: typedAgentPhoneNumber,
      backupOffer: listing.flip_state === 'resale_in_contract',
      delayedOfferResponsePilotResponseDay: listing.delayed_offer_response_pilot_response_day,
      homeAdvisorEmail: listing.address.market.buyer_contact_email,
      homeAdvisorName: listing.resale_agent_lead.full_name,
      inDelayedOfferResponsePilotPeriod: listing.in_delayed_offer_response_pilot_period,
      listingOffer,
      register,
      registrationRequestStatus,
      slug: listing.slug,
    };
  };
}

/****** CONNECT OFFER PORTAL TO REDUX ********/

const mapStateToProps = (state): Partial<IAgentResaleOfferActions> => ({
  agent: agentResalePortalSelectors.getAgent(state),
  isLoadingAgent: agentResalePortalSelectors.getIsLoadingAgent(state),
  isLoadingListing: agentResalePortalSelectors.getIsLoadingListing(state),
  listing: agentResalePortalSelectors.getListing(state),
  listingOffer: agentResalePortalSelectors.setListingOffer(state),
  offerSubmissionCompleted: agentResalePortalSelectors.getOfferSubmissionCompleted(state),
  pendingDocumentUploads: agentResalePortalSelectors.getPendingDocumentUploads(state),
  registrationRequestStatus: sessionSelectors.registrationRequestStatus(state),
  typedAgentEmail: agentResalePortalSelectors.getTypedAgentEmail(state),
  typedAgentFullName: agentResalePortalSelectors.getTypedAgentFullName(state),
  typedAgentPhoneNumber: agentResalePortalSelectors.getTypedAgentPhoneNumber(state),
  ...agentResalePortalSelectors.getErrors(state),
  ...agentResalePortalSelectors.getOfferDetails(state),
});

const mapDispatchToProps = (dispatch: any): Partial<IAgentResaleOfferActions> => {
  return {
    deleteDocument: (fileTypeId: string | number) => {
      dispatch(agentResalePortalActions.deleteDocument(fileTypeId));
    },
    getAgent: (agentFullName: string, agentPhoneNumber: string) => {
      dispatch(agentResalePortalActions.getAgent(agentFullName, agentPhoneNumber));
    },
    getListing: (listingSlug: string) => {
      dispatch(agentResalePortalActions.getListing(listingSlug));
    },
    register: (email: string, name: string, password: string, phone: string, rememberMe: boolean) =>
      dispatch(sessionActions.register(email, name, password, phone, rememberMe)),
    setTypedAgentEmail: (typedAgentEmail: string) => {
      dispatch(agentResalePortalActions.setTypedAgentEmail(typedAgentEmail));
    },
    setTypedAgentFullName: (typedAgentFullName: string) => {
      dispatch(agentResalePortalActions.setTypedAgentFullName(typedAgentFullName));
    },
    setTypedAgentPhoneNumber: (typedAgentPhoneNumber: string) => {
      dispatch(agentResalePortalActions.setTypedAgentPhoneNumber(typedAgentPhoneNumber));
    },
    submitListingOffer: (offerDetails: IOfferDetailPageOnSubmitParams) => {
      dispatch(agentResalePortalActions.submitListingOffer(offerDetails));
    },
    uploadDocument: (document: { file: File; s3Key: string }, fileTypeId?: number | string) => {
      dispatch(
        agentResalePortalActions.uploadDocument(document, 'resale-offer-portal', fileTypeId),
      );
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AgentResaleOfferPortal);
